import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseLanguageModelInput } from '@langchain/core/language_models/base';
import { ChatOpenAIFields } from '@langchain/openai';
import { GTPChatModelType } from '@shared/index';
import { firstValueFrom } from 'rxjs';
import { EventManagerAdminService } from '../../event-manager/event-manager-admin.service';
import { BotidService } from '@shared-angular/services/ragai/botid.service';

@Injectable({
  providedIn: 'root'
})
export class OpenaiAdminService {

  constructor(
    private eventManager: EventManagerAdminService,
    private http: HttpClient,
    private botIdService: BotidService) {
  }

  private async _complexCompletion(modelConf: ChatOpenAIFields, promptInput: BaseLanguageModelInput, llmType: string, jsonResponse: boolean, webSearch: boolean = false) {
    this.eventManager.triggerEventHook("io_out_chatcompletion_start", { modelConf, promptInput, llmType, jsonResponse, webSearch });
    const result = await firstValueFrom(this.http.post("/api/toolbox/completion/create/" + llmType + "/" + this.botIdService.getBotId(),
      {
        modelConf,
        promptInput,
        jsonResponse,
        webSearch
      }
    ));
    this.eventManager.triggerEventHook("io_in_chatcompletion_end", { modelConf, promptInput, llmType, jsonResponse, webSearch, result });
    return result;
  }

  public getImageMesageContent(src: string) {
    if (!src.startsWith('http')) {
      src = window.RAGAI.HELPER.getImageURlFromSkill(src);
    }

    return {
      type: "image_url",
      image_url:
      {
        url: src,
        detail: "auto"
      }
    };
  }

  public async chatCompletion(input: string, model: GTPChatModelType = "gpt-4.1", temperature: number = .7, webSearch: boolean = false) {
    const modelConf: ChatOpenAIFields = {
      model,
      temperature
    };
    const promptInput: BaseLanguageModelInput = input;
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.AITEXT_OPENAI.CHATCOMPLETION.request", { request: { modelConf, promptInput, webSearch } });
    const result = await this._complexCompletion(modelConf, promptInput, "openai", false, webSearch);
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.AITEXT_OPENAI.CHATCOMPLETION.result", { result, request: { modelConf, promptInput, webSearch } });
    return result;
  }


  public async chatCompletionJson(input: string, model: GTPChatModelType = "gpt-4.1", temperature: number = .7, webSearch: boolean = false) {
    const modelConf: ChatOpenAIFields = {
      model,
      temperature
    };
    const promptInput: BaseLanguageModelInput = input;
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.AITEXT_OPENAI.CHATCOMPLETION.request", { request: { modelConf, promptInput, webSearch } });
    const result = await this._complexCompletion(modelConf, promptInput, "openai", true, webSearch);
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.AITEXT_OPENAI.CHATCOMPLETION.result", { result, request: { modelConf, promptInput, webSearch } });
    return result;
  }

  public async structuredResponse<T = unknown>(options: {
    promptInput: string;
    jsonSchema: { name: string; schema: Record<string, unknown> };
    model?: GTPChatModelType;
    temperature?: number;
    instructions?: string;
    webSearch?: boolean;
  }): Promise<T> {
    const {
      promptInput,
      jsonSchema,
      model = "gpt-4o-mini",
      temperature = 0.2,
      instructions,
      webSearch = false
    } = options;
    const modelConf: ChatOpenAIFields = { model, temperature };
    const body = { modelConf, promptInput, jsonSchema, webSearch, ...(instructions != null ? { instructions } : {}) };
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.AITEXT_OPENAI.STRUCTURED_RESPONSE.request", { request: body });
    const result = await firstValueFrom(
      this.http.post<T>("/api/toolbox/responses/create/" + this.botIdService.getBotId(), body)
    );
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.AITEXT_OPENAI.STRUCTURED_RESPONSE.result", { result, request: body });
    return result;
  }
}
